import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import API_BASE_URL from '../config/baseUrl';

const MatkaListPage = () => {
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: '2026-01-12',
    endDate: '2026-01-27'
  });
  const [matkaMatches, setMatkaMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lockUpdatingId, setLockUpdatingId] = useState(null);

  const { accessToken, user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.userTypeInfo === 7;

  // Fetch matka matches on component mount
  useEffect(() => {
    fetchMatkaMatches();
  }, []);

  const fetchMatkaMatches = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/matka/list`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.status) {
        setMatkaMatches(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch matka matches');
      }
    } catch (err) {
      console.error('Error fetching matka matches:', err);
      setError('Failed to load matka matches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    // You can add date filtering logic here if needed
    fetchMatkaMatches();
  };

  const handleToggleBetLock = async (match) => {
    if (!match?.id) return;
    const nextLock = !match.isBetLock;
    setLockUpdatingId(match.id);
    setError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/matka/matka-betting-lkunlk`,
        { matkaId: match.id, betLock: nextLock },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.status) {
        setMatkaMatches((prev) =>
          prev.map((item) =>
            item.id === match.id ? { ...item, isBetLock: nextLock } : item,
          ),
        );
      } else {
        setError(response.data?.message || "Failed to update bet lock");
      }
    } catch (err) {
      setError("Failed to update bet lock. Please try again.");
    } finally {
      setLockUpdatingId(null);
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-[#0F2327] p-4 flex items-center justify-between mb-4">
        <span className="text-white text-2xl font-normal uppercase">Matka</span>
        <button
          type="button"
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 transition-colors"
        >
          <span>Back</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-4 mb-4 p-3 bg-red-500 text-white rounded">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Matka Match Cards */}
          <div className="flex flex-wrap gap-2 mb-6">
            {matkaMatches.map((match) => (
              <div
                key={match.id}
                className="px-2 py-2 w-full sm:w-[calc(50%-8px)]"
              >
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <Link to={`/sports-betting/matka/${match.id}`} className="block">
                    <div className="bg-blue-500 py-2 px-3 text-white text-sm uppercase font-semibold flex justify-start items-center gap-2 hover:bg-blue-600 transition-colors">
                      <span>{match.name}</span>
                      <span className="text-xs">{match.time}</span>
                    </div>
                  </Link>
                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => handleToggleBetLock(match)}
                      disabled={lockUpdatingId === match.id}
                      className={
                        "px-2.5 py-0.5 text-[9px] font-semibold uppercase text-white leading-none whitespace-nowrap " +
                        (match.isBetLock
                          ? "bg-green-500 hover:bg-green-600"
                          : "bg-red-500 hover:bg-red-600") +
                        (lockUpdatingId === match.id
                          ? " opacity-60 cursor-not-allowed"
                          : "")
                      }
                    >
                      {match.isBetLock ? "Unlock" : "Lock"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Date Range Filter */}
      <div className="flex items-center gap-6 px-5 pt-4 mb-4">
        <div className="border border-gray-300 flex items-center">
          <div className="px-3 py-2 bg-white">
            <input
              type="date"
              value={selectedDateRange.startDate}
              onChange={(e) => setSelectedDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="outline-none"
            />
          </div>
          <div className="px-2 py-2 flex items-center">
            <svg viewBox="0 0 1024 1024" width="1em" height="1em" fill="currentColor">
              <path d="M873.1 596.2l-164-208A32 32 0 00684 376h-64.8c-6.7 0-10.4 7.7-6.3 13l144.3 183H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h695.9c26.8 0 41.7-30.8 25.2-51.8z"></path>
            </svg>
          </div>
          <div className="px-3 py-2 bg-white">
            <input
              type="date"
              value={selectedDateRange.endDate}
              onChange={(e) => setSelectedDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="outline-none"
            />
          </div>
          <div className="px-3 py-2">
            <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
              <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zM184 392V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136H184z"></path>
            </svg>
          </div>
        </div>
        <div>
          <button
            type="button"
            onClick={handleSubmit}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 transition-colors"
          >
            <span>Submit</span>
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-300 px-4 py-3 text-left text-sm">Event Name</th>
              <th className="border border-gray-300 px-4 py-3 text-left text-sm">Date & Time</th>
              <th className="border border-gray-300 px-4 py-3 text-left text-sm">P/L</th>
              <th className="border border-gray-300 px-4 py-3 text-left text-sm">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-gray-100">
              <td className="border border-gray-300 px-4 py-2">Total</td>
              <td className="border border-gray-300 px-4 py-2"></td>
              <td className="border border-gray-300 px-4 py-2">
                <span className="text-red-600">0.00</span>
              </td>
              <td className="border border-gray-300 px-4 py-2"></td>
            </tr>
            {/* Add more rows here when data is available */}
          </tbody>
        </table>

        {/* Empty State */}
        <div className="flex flex-col items-center justify-center py-12 bg-white border border-t-0 border-gray-300">
          <svg className="w-16 h-16 text-gray-300 mb-4" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(0 1)" fill="none" fillRule="evenodd">
              <ellipse className="fill-current opacity-25" cx="32" cy="33" rx="32" ry="7"></ellipse>
              <g className="fill-current opacity-25" fillRule="nonzero">
                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"></path>
              </g>
            </g>
          </svg>
          <p className="text-gray-500">No data</p>
        </div>
      </div>
    </div>
  );
};

export default MatkaListPage;
